import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Paid } from "./Admin";
import { ClientLink } from "./AdminClient";
import { toast } from "sonner";
import { Search, Send, ChevronDown, ChevronRight, Users, Mail, Eye, CheckCircle2, BellRing, PlayCircle, Pencil, Activity } from "lucide-react";

const PROGRAMME_LABEL = {
  essentiel: "Essentiel",
  epistolaire: "Épistolaire",
};

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-[#F5F0E6] p-5 border border-[#E8DCC8]">
      <div className="flex items-center gap-2 text-[#7C7064] text-xs uppercase tracking-wider mb-2">
        <Icon size={14}/> {label}
      </div>
      <p className="font-serif text-3xl text-[#2C2621]">{value}</p>
      {hint && <p className="text-xs text-[#7C7064] mt-1">{hint}</p>}
    </div>
  );
}

function ParticipantRow({ p, onResend }) {
  const [open, setOpen] = useState(false);
  const [resending, setResending] = useState(null);
  const totalWeeks = 52; // Les deux programmes suivent la même drip de 52 thèmes hebdomadaires
  // La progression réelle = dernière semaine envoyée (mise à jour par le cron).
  // `current_week` peut être obsolète car il n'est initialisé qu'à l'inscription.
  const currentSentWeek = Math.max(p.last_sent_week || 0, p.current_week || 0);
  const progressPct = Math.min(100, Math.round((currentSentWeek / totalWeeks) * 100));
  const history = p.history || [];
  const sentWeeks = new Set(history.map(h => h.week));

  async function doResend(week) {
    if (!window.confirm(`Renvoyer l'email de la semaine ${week} à ${p.customer_name || p.email} ?`)) return;
    setResending(week);
    try {
      await api.post(`/admin/programme/participants/${encodeURIComponent(p.email)}/resend-week`, { week });
      toast.success(`Semaine ${week} envoyée à ${p.email}`);
      onResend?.();
    } catch (e) {
      if (e.response?.status === 409) {
        // Anti-doublon: already sent. Ask to force.
        const force = window.confirm(
          `⚠️ ${e.response?.data?.detail || "Cette semaine a déjà été envoyée."}\n\n` +
          `Veux-tu VRAIMENT la renvoyer ?\n\n` +
          `(OK = forcer le renvoi. La participante recevra un 2e email pour cette semaine.)`
        );
        if (force) {
          try {
            await api.post(`/admin/programme/participants/${encodeURIComponent(p.email)}/resend-week`, { week, force: true });
            toast.success(`Semaine ${week} renvoyée (forcée) à ${p.email}`);
            onResend?.();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur d'envoi forcé");
          }
        }
      } else {
        toast.error(e.response?.data?.detail || "Erreur d'envoi");
      }
    } finally {
      setResending(null);
    }
  }

  return (
    <>
      <tr className="border-t border-[#E8DCC8] hover:bg-[#FDFBF7]">
        <td className="p-3">
          <button data-testid={`prog-track-toggle-${p.email}`} onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1 text-[#4A4036] hover:text-[#A3876A]">
            {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            <span className="font-medium">{p.customer_name || "—"}</span>
          </button>
        </td>
        <td className="p-3 text-xs text-[#7C7064]"><ClientLink email={p.email}/></td>
        <td className="p-3 text-xs">{PROGRAMME_LABEL[p.programme_type] || p.programme_type}</td>
        <td className="p-3" data-testid={`paid-${p.email}`}><Paid cents={p.amount_paid} at={p.paid_at}/></td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-24 bg-[#E8DCC8] overflow-hidden">
              <div className="h-full bg-[#A3876A]" style={{ width: `${progressPct}%` }}/>
            </div>
            <span className="text-xs text-[#7C7064]">{currentSentWeek}/{totalWeeks}</span>
            <button data-testid={`prog-track-edit-week-${p.email}`}
              onClick={async (e) => {
                e.stopPropagation();
                const val = window.prompt(`Marquer ${p.customer_name || p.email} comme ayant reçu jusqu'à la semaine…\n(0 = n'a rien reçu, 52 = programme terminé)`, String(currentSentWeek));
                if (val === null) return;
                const week = parseInt(val, 10);
                if (isNaN(week) || week < 0 || week > 52) {
                  toast.error("Numéro de semaine invalide (0-52)");
                  return;
                }
                const markSentNow = window.confirm(`Positionner aussi la date du dernier envoi à maintenant ?\n\nOK = oui (empêche un doublon si le cron tourne dans les 3 jours qui viennent)\nAnnuler = non (le cron enverra la suite dimanche prochain sans garde-fou)`);
                try {
                  await api.post(`/admin/programme/participants/${encodeURIComponent(p.email)}/set-week`, { week, mark_sent_now: markSentNow });
                  toast.success(`${p.customer_name || p.email} → semaine ${week}`);
                  onResend?.();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erreur");
                }
              }}
              className="text-[#7C7064] hover:text-[#A3876A]" title="Ajuster la semaine actuelle">
              <Pencil size={12}/>
            </button>
          </div>
        </td>
        <td className="p-3 text-xs">
          {p.status === "finished"
            ? <span className="text-[#7C7064]">Terminé</span>
            : p.status === "paused"
              ? <span className="text-[#B87333]">En pause</span>
              : <span className="text-[#5C7A5C]">Actif</span>}
        </td>
        <td className="p-3 text-xs text-[#7C7064]">
          {p.last_sent_at
            ? new Date(p.last_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
            : "—"}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#FDFBF7]">
          <td colSpan={7} className="p-4">
            <div className="pl-6">
              <p className="text-xs uppercase tracking-wider text-[#7C7064] mb-3">Historique & renvois manuels</p>
              <div className="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-13 gap-2">
                {Array.from({ length: totalWeeks }).map((_, i) => {
                  const week = i + 1;
                  const sent = history.find(h => h.week === week);
                  const isSent = !!sent;
                  const isOpened = !!sent?.opened_at;
                  const isCurrent = week === currentSentWeek + 1 && p.status === "active";
                  return (
                    <div key={week}
                      className={`relative border p-2 text-xs text-center ${
                        isOpened ? "bg-[#DCE8DC] border-[#7A9A7A]"
                        : isSent ? "bg-[#F5F0E6] border-[#A3876A]"
                        : isCurrent ? "bg-white border-[#B87333] border-dashed"
                        : "bg-white border-[#E8DCC8]"}`}
                      title={sent ? `Envoyé le ${new Date(sent.sent_at).toLocaleString("fr-FR")}${sent.opened_at ? " • ouvert" : ""}${sent.source === "manual_resend" ? " • renvoi manuel" : ""}` : ""}
                    >
                      <p className="font-medium text-[#2C2621]">S{week}</p>
                      {isOpened && <Eye size={10} className="mx-auto text-[#5C7A5C] mt-1"/>}
                      {!isOpened && isSent && <CheckCircle2 size={10} className="mx-auto text-[#A3876A] mt-1"/>}
                      <button
                        data-testid={`prog-track-resend-${p.email}-w${week}`}
                        onClick={() => doResend(week)}
                        disabled={resending === week}
                        className="mt-1 text-[10px] text-[#A86962] hover:underline disabled:opacity-50"
                      >
                        {resending === week ? "…" : "Renvoyer"}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 text-[11px] text-[#7C7064]">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#DCE8DC] border border-[#7A9A7A]"/> Ouvert</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#F5F0E6] border border-[#A3876A]"/> Envoyé</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-white border border-[#B87333] border-dashed"/> À venir dimanche</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-white border border-[#E8DCC8]"/> Pas encore envoyé</span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminProgrammeTracking() {
  const [data, setData] = useState({ stats: null, participants: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/programme/tracking");
      setData(r.data);
    } catch (e) {
      toast.error("Impossible de charger le suivi");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.participants;
    return data.participants.filter(p =>
      (p.customer_name || "").toLowerCase().includes(term) ||
      (p.email || "").toLowerCase().includes(term)
    );
  }, [q, data.participants]);

  const stats = data.stats || {};

  return (
    <div data-testid="admin-programme-tracking" className="max-w-6xl">
      <p className="font-hand text-xl text-[#A3876A] mb-2">Accompagner semaine après semaine</p>
      <h1 className="font-serif text-4xl mb-10">Suivi des programmes d'écriture</h1>

      {/* STATS */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Actives — Essentiel" value={loading ? "…" : (stats.active_essentiel ?? 0)}/>
        <StatCard icon={Users} label="Actives — Épistolaire" value={loading ? "…" : (stats.active_epistolaire ?? 0)}/>
        <StatCard icon={Send} label="Envoyés cette semaine" value={loading ? "…" : (stats.sent_this_week ?? 0)}
          hint="Emails du programme envoyés (7 derniers jours)"/>
        <StatCard icon={Eye} label="Taux d'ouverture" 
          value={loading ? "…" : (stats.open_rate_30d != null ? `${stats.open_rate_30d}%` : "—")}
          hint={stats.open_rate_30d != null ? `Sur ${stats.opens_sample_size} envois (30j)` : "Données Resend indisponibles"}/>
      </div>

      {/* SEARCH */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C7064]"/>
          <input
            data-testid="prog-track-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Rechercher par nom ou email…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-[#E8DCC8] text-sm outline-none focus:border-[#A3876A]"
          />
        </div>
        <button data-testid="prog-track-run-weekly" onClick={async () => {
          if (!window.confirm("Envoyer maintenant à chaque participante active sa prochaine semaine (rattrapage du dimanche) ?")) return;
          try {
            await api.post("/admin/programme/weekly/run");
            toast.success("Envoi lancé. Les emails partent dans les prochaines minutes.");
            setTimeout(load, 8000);
          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        }}
          className="flex items-center gap-1.5 text-sm text-white bg-[#A3876A] hover:bg-[#8f7457] border border-[#A3876A] px-3 py-2">
          <PlayCircle size={13}/> Envoyer maintenant les thèmes
        </button>
        <button data-testid="prog-track-run-reminder" onClick={async () => {
          if (!window.confirm("Envoyer maintenant une relance douce aux participantes qui n'ont ouvert aucun de leurs 3 derniers thèmes ?")) return;
          try {
            await api.post("/admin/programme/open-reminder/run");
            toast.success("Relance lancée. Un mail part vers chaque participante concernée dans les prochaines minutes.");
          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        }}
          className="flex items-center gap-1.5 text-sm text-[#A3876A] hover:text-[#2C2621] border border-[#E8DCC8] px-3 py-2 bg-white">
          <BellRing size={13}/> Envoyer relance (non-ouverts)
        </button>
        <button data-testid="prog-track-refresh" onClick={load}
          className="text-sm text-[#A3876A] hover:underline">
          Rafraîchir
        </button>
      </div>

      <p className="text-xs text-[#7C7064] mb-2">
        <BellRing size={12} className="inline mr-1"/>
        Envoi automatique chaque <strong>dimanche à 9h heure de Paris</strong>. Une inscription jusqu'au samedi soir reçoit son premier thème le dimanche même ; à partir du dimanche 7h, l'inscription attend le dimanche suivant. Une relance douce part le <strong>mercredi</strong> vers celles qui n'ont ouvert aucun de leurs 3 derniers thèmes (cooldown 4 semaines).
      </p>

      {/* CRON RUN HISTORY */}
      {!loading && data.cron_runs && data.cron_runs.length > 0 && (
        <div data-testid="prog-track-cron-runs" className="bg-white border border-[#E8DCC8] mb-6">
          <div className="px-4 py-3 border-b border-[#E8DCC8] bg-[#F5F0E6] flex items-center gap-2">
            <Activity size={14} className="text-[#A3876A]"/>
            <span className="font-serif text-sm text-[#2C2621]">Dernières exécutions du dimanche</span>
          </div>
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wider text-[#7C7064]">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2 text-right">Actives</th>
                <th className="px-4 py-2 text-right">Envoyés</th>
                <th className="px-4 py-2 text-right">Passés</th>
                <th className="px-4 py-2 text-right">Échecs</th>
                <th className="px-4 py-2 text-right">Durée</th>
              </tr>
            </thead>
            <tbody>
              {data.cron_runs.map(r => {
                const dt = new Date(r.started_at);
                const dateStr = dt.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
                return (
                  <tr key={r.run_id} className="border-t border-[#E8DCC8]">
                    <td className="px-4 py-2 text-[#4A4036]">{dateStr}</td>
                    <td className="px-4 py-2 text-right">{r.total_active}</td>
                    <td className="px-4 py-2 text-right font-serif text-[#2C2621]"><strong>{r.sent}</strong></td>
                    <td className="px-4 py-2 text-right text-[#7C7064]">{r.skipped}</td>
                    <td className={`px-4 py-2 text-right ${r.failed > 0 ? "text-[#B04A3C] font-serif" : "text-[#7C7064]"}`}>
                      {r.failed > 0 ? <strong>{r.failed}</strong> : "0"}
                    </td>
                    <td className="px-4 py-2 text-right text-[#7C7064]">{r.duration_s}s</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-4 py-2 text-[10px] text-[#7C7064] italic border-t border-[#E8DCC8]">
            Un email récap est envoyé à Amélie après chaque exécution avec au moins un envoi ou un échec.
          </p>
        </div>
      )}

      {/* TABLE */}
      <div className="bg-white border border-[#E8DCC8]">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left text-xs uppercase tracking-wider text-[#7C7064]">
            <tr>
              <th className="p-3">Prénom</th>
              <th className="p-3">Email</th>
              <th className="p-3">Programme</th>
              <th className="p-3">Payé</th>
              <th className="p-3">Progression</th>
              <th className="p-3">Statut</th>
              <th className="p-3">Dernier envoi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-[#7C7064] text-sm">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#7C7064] text-sm">
                {q ? "Aucune participante ne correspond à ta recherche." : "Aucune participante inscrite pour le moment."}
              </td></tr>
            )}
            {!loading && filtered.map(p => (
              <ParticipantRow key={p.email} p={p} onResend={load}/>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#7C7064] mt-4">
        <Mail size={12} className="inline mr-1"/>
        Clique sur un prénom pour voir toutes les semaines. Le bouton “Renvoyer” envoie l'email de la semaine choisie immédiatement, sans faire progresser le programme.
      </p>
    </div>
  );
}
